'use client'

import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { CloseIcon } from '../icons/CloseIcon'

interface ModalProps {
  open: boolean
  onClose: () => void
  /** Announced to screen readers and rendered as the heading. */
  title: string
  children: ReactNode
  /** Action row, usually a cancel plus a primary button. */
  footer?: ReactNode
  /** Overrides the accessible name when it should differ from the visible title. */
  ariaLabel?: string
  /** Set on the root so each caller keeps its own identity in the DOM. */
  dataComponent?: string
}

/**
 * The dialog shell shared by every modal in the app. Extracted from
 * GatedAccessModal and ClipPaywallModal, which had drifted into identical
 * copies of the same markup.
 *
 * Adds Escape-to-close and background scroll locking, which none of the
 * hand-rolled copies had. Focus is not trapped — worth adding if these ever
 * carry a form, but every current use is read-then-dismiss.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  ariaLabel,
  dataComponent = 'Modal',
}: ModalProps) {
  useEffect(() => {
    if (!open) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }

    document.addEventListener('keydown', onKeyDown)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel ?? title}
      data-component={dataComponent}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      {/* Backdrop */}
      <div
        data-part="backdrop"
        className="absolute inset-0 bg-ink/40 dark:bg-navy/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        data-part="panel"
        className="relative z-10 w-full max-w-md rounded-2xl border border-carolina/[0.12]
          bg-white dark:bg-navy shadow-xl px-6 py-6"
      >
        <div data-part="header" className="flex items-start justify-between gap-4 mb-4">
          <h2 className="font-bold text-[18px] text-ink dark:text-cream">{title}</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 text-mist dark:text-cream/30 hover:text-ink dark:hover:text-cream
              transition-colors mt-0.5"
          >
            <CloseIcon />
          </button>
        </div>

        <div data-part="body" className="text-[14px] text-steel dark:text-mist mb-6">
          {children}
        </div>

        {footer && (
          <div data-part="footer" className="flex gap-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}
